import { PSD2_FIELD_SOURCE } from "~/utils/const";
import { GO, PREVIOUS, PSD2_STATE } from "~/utils/psd2/constants";

import Psd2StateLoading from "~/components/psd2/states/Loading.vue";
import Psd2StateIntro from "~/components/psd2/Intro.vue";
import Psd2StateBankSelect from "~/components/psd2/states/BankSelect.vue";
import Psd2StateInProgress from "~/components/psd2/states/InProgress.vue";
import Psd2StateFailed from "~/components/psd2/states/Failed.vue";
import Psd2StateResult from "~/components/psd2/Results.vue";

/**
 * Every field a state may declare, and what it means when it does not.
 *
 * A state that declares nothing is a plain screen with a working next button,
 * no way back and no title - which is why most entries below are two lines.
 */
const STATE_DEFAULTS = Object.freeze({
  /** Where the flow sits before it has heard from the backend. Exactly one. */
  initial: false,
  /** Part of the forward walk. Only linear states are reachable by GO.NEXT. */
  linear: false,
  /**
   * Where "let me try another bank" leads. Declared by the states that offer
   * it: cancelling a running sync, and adding a second account to a finished
   * one. The same edge as a failure's `next`, so it is named the same way.
   */
  restartsAt: null,
  /** Token key. Rendered as a page header on the business screens only. */
  title: null,
  /** Token key for the line under the title. Ignored without a title. */
  subtitle: null,
  /** `{ title, description }` token keys for a banner above the screen. */
  notice: null,
  /** Token key for the next button. null keeps the host's own generic label. */
  nextLabel: null,
  previous: PREVIOUS.NONE,
  /**
   * The sync is over and this is how it went: stop polling, drop the marker
   * that says one is running, and tell the chat widget we are on a summary.
   */
  terminal: false,
  /**
   * Whether reaching this state hands the flow onward immediately instead
   * of waiting for the customer to press next. The state is still defined
   * and still reachable (a restart lands back on it) - it is just never
   * the one rendered on screen.
   */
  autoAdvances: () => false,
  /** Whether to keep asking the backend how the sync is going. */
  polls: false,
  /** Whether the footer is drawn at all. A screen with nothing to click says no. */
  showsActionArea: () => true,
  showsNext: () => true,
  showsSkip: () => false,
  nextDisabled: () => false,
  /**
   * @param flow the reactive context from usePsd2Flow
   * @returns {string|Promise<string>} a PSD2_STATE id, or a GO sentinel
   */
  next: () => GO.NEXT,
});

/**
 * Three states end the same way: hand the customer to their bank. The browser
 * leaves this page inside `startSync`, so there is nowhere to move to.
 */
const startSync = async (flow) => {
  await flow.startSync();
  return GO.STAY;
};

const needsBank = (flow) => !flow.bankSelect;

/**
 * The flow, in order.
 *
 * Adding a step: add one entry here and one component under
 * components/psd2/states/. Removing a step: delete the entry, its component,
 * and the `PSD2_STATE` member naming it. What a status *means* lives in
 * status.js, and it names states too - so a step the backend can put you in
 * needs a line there as well.
 *
 * Nothing else names a step, except where the flow reacts to something the
 * backend said rather than to something the customer did: the composable names
 * FAILED for a sync it could not even start and LIMIT_REACHED for the
 * aggregator's daily cap, and `resolveState` names FAILED as the screen an id
 * nobody declared falls back to.
 *
 * The order matters. The forward and backward walks below read it, so no code
 * adds or subtracts one from a step, and reordering here is a safe edit.
 */
const PSD2_STATES = Object.freeze([
  {
    id: PSD2_STATE.LOADING,
    component: Psd2StateLoading,
    initial: true,
    // We draw no footer of our own while loading. A host that draws one
    // still gets a next button, disabled: it has nowhere to put a gap.
    showsActionArea: () => false,
    nextDisabled: () => true,
    next: () => GO.STAY,
  },
  {
    id: PSD2_STATE.INTRO,
    component: Psd2StateIntro,
    linear: true,
    // No title: the intro draws its own header, because its subtitle is
    // part of the copy rather than part of the chrome.
    nextLabel: "psd2.intro.button.next",
    previous: PREVIOUS.LEAVE,
    showsSkip: (flow) => flow.skippable,
  },
  {
    id: PSD2_STATE.BANK_SELECT,
    component: Psd2StateBankSelect,
    linear: true,
    title: "psd2.bank_select.title",
    subtitle: "psd2.bank_select.subtitle",
    previous: PREVIOUS.STEP,
    nextDisabled: needsBank,
    next: startSync,
  },
  {
    id: PSD2_STATE.IN_PROGRESS,
    component: Psd2StateInProgress,
    title: "psd2.result.title",
    polls: true,
    restartsAt: PSD2_STATE.BANK_SELECT,
    nextDisabled: () => true,
    next: () => GO.STAY,
  },
  {
    id: PSD2_STATE.FAILED,
    component: Psd2StateFailed,
    title: "psd2.result.title",
    terminal: true,
    // Let them pick a bank and try again.
    next: () => PSD2_STATE.BANK_SELECT,
  },
  {
    id: PSD2_STATE.RESULT,
    component: Psd2StateResult,
    title: "psd2.result.title",
    terminal: true,
    restartsAt: PSD2_STATE.BANK_SELECT,
    // A finished correction has nowhere further to go: the todo is answered
    // by the sync itself.
    showsNext: (flow) => flow.source !== PSD2_FIELD_SOURCE.CORRECTION,
    // The salary-and-"sync another bank" screen is skipped: a completed
    // sync hands straight back to whatever the host shows next, the same
    // place its own next button used to lead. A correction still has
    // nowhere further to go, so it stays put and shows the figures.
    autoAdvances: (flow) => flow.source !== PSD2_FIELD_SOURCE.CORRECTION,
    next: () => GO.LEAVE,
  },
  {
    id: PSD2_STATE.ACCOUNT_MISMATCH,
    component: Psd2StateBankSelect,
    title: "psd2.result.title",
    terminal: true,
    notice: {
      title: "psd2.wrong_bank_account.title",
      description: "psd2.wrong_bank_account.description",
    },
    nextDisabled: needsBank,
    next: startSync,
  },
  {
    id: PSD2_STATE.LIMIT_REACHED,
    component: Psd2StateBankSelect,
    // Copy both segments share, so it names its own namespace.
    notice: {
      title: "psd2:sync_limit_reached.title",
      description: "psd2:sync_limit_reached.description",
    },
    nextDisabled: needsBank,
    next: startSync,
  },
]);

const STATES = PSD2_STATES.map((state) =>
  Object.freeze({ ...STATE_DEFAULTS, ...state })
);
const BY_ID = new Map(STATES.map((state) => [state.id, state]));
const LINEAR = STATES.filter((state) => state.linear);

/** Where an id nobody declared sends the customer instead. */
const FALLBACK_ID = PSD2_STATE.FAILED;

/** Where the flow sits before it has heard from the backend. */
export const INITIAL_STATE_ID = STATES.find((state) => state.initial).id;

/** The first screen the customer is actually asked to do something on. */
export const START_STATE_ID = LINEAR[0].id;

/**
 * An id nobody declared resolves to the failure screen, never to nothing - a
 * state that names no `restartsAt`, say. Anything else is a defect in this
 * registry or in status.js, and nothing else would surface it.
 */
export function resolveState(id) {
  let state = BY_ID.get(id);
  if (state) {
    return state;
  }
  console.error("PSD2:: no such state, falling back", id);
  return BY_ID.get(FALLBACK_ID);
}

export function stateAfter(id) {
  let index = LINEAR.findIndex((state) => state.id === id);
  return index < 0 ? null : LINEAR[index + 1]?.id ?? null;
}

export function stateBefore(id) {
  let index = LINEAR.findIndex((state) => state.id === id);
  return index > 0 ? LINEAR[index - 1].id : null;
}

/**
 * Which buttons the footer offers, and whether the customer may use them.
 *
 * The B2B pages read this off the component rather than working it out again,
 * so there is one answer to "is next enabled" per state.
 *
 * @param host what the surrounding page allows: whether these screens draw
 *             their own footer, and whether it may offer a way back.
 */
export function actionAreaFor(id, flow, host) {
  let state = resolveState(id);
  return {
    // Whether *we* draw the footer. A host that draws its own reads the
    // buttons below and ignores this.
    visible: host.ownsActionArea && state.showsActionArea(flow),
    skip: state.showsSkip(flow),
    previous: offersPrevious(state, host),
    next: {
      visible: state.showsNext(flow),
      disabled: flow.syncStarted || state.nextDisabled(flow),
      // null means "the host's own generic label", so B2C keeps
      // bnpl:next and the B2B pages keep their arrow button.
      label: flow.token(state.nextLabel),
    },
  };
}

function offersPrevious(state, host) {
  if (state.previous === PREVIOUS.LEAVE) {
    return host.canLeaveBackwards;
  }
  if (state.previous === PREVIOUS.STEP) {
    return !!stateBefore(state.id) && host.canStepBackwards;
  }
  return false;
}
